import type { ExerciseAttempt, LearningSession, MasteryRecord } from '@/domain/records';

/**
 * Achievements reward learning behaviours (practising, persevering, reviewing,
 * mastering) — never speed, streaks or comparison with other children.
 */
export interface AchievementStats {
  completed: LearningSession[];
  attempts: ExerciseAttempt[];
  mastery: MasteryRecord[];
}

export interface AchievementDefinition {
  id: string;
  icon: string;
  /** Progress towards the goal, as [current, target]. */
  progress: (stats: AchievementStats) => [number, number];
}

const sessionsOf = (stats: AchievementStats, subjectId: string) =>
  stats.completed.filter((s) => s.subjectId === subjectId).length;

export const ACHIEVEMENTS: AchievementDefinition[] = [
  { id: 'first-steps', icon: '🌱', progress: (s) => [s.completed.length, 1] },
  { id: 'math-explorer', icon: '🔢', progress: (s) => [sessionsOf(s, 'math'), 5] },
  { id: 'letter-friend', icon: '📖', progress: (s) => [sessionsOf(s, 'arabic'), 5] },
  { id: 'curious-scientist', icon: '🔬', progress: (s) => [sessionsOf(s, 'science'), 5] },
  {
    id: 'globe-trotter',
    icon: '🗺️',
    progress: (s) => [new Set(s.completed.map((x) => x.subjectId).filter(Boolean)).size, 3],
  },
  {
    id: 'never-give-up',
    icon: '💪',
    progress: (s) => [s.attempts.filter((a) => a.attemptNumber > 1 && a.correct).length, 10],
  },
  {
    id: 'good-helper',
    icon: '💡',
    progress: (s) => [s.attempts.filter((a) => a.hintUsed && a.correct).length, 5],
  },
  {
    id: 'first-flower',
    icon: '🌼',
    progress: (s) => [s.mastery.filter((m) => m.status === 'provisionalMastery').length, 1],
  },
  {
    id: 'gardener',
    icon: '🌻',
    progress: (s) => [s.mastery.filter((m) => m.status === 'provisionalMastery').length, 5],
  },
  {
    id: 'mission-1',
    icon: '⭐',
    progress: (s) => [s.completed.filter((x) => x.gameId === 'daily-mission').length, 1],
  },
  {
    id: 'mission-7',
    icon: '🌟',
    progress: (s) => [s.completed.filter((x) => x.gameId === 'daily-mission').length, 7],
  },
  {
    id: 'reviewer',
    icon: '🔁',
    progress: (s) => [s.completed.filter((x) => x.isReview).length, 3],
  },
  {
    id: 'careful-work',
    icon: '🏅',
    progress: (s) => [s.completed.filter((x) => x.stars === 3).length, 1],
  },
];

export function unlockedIds(stats: AchievementStats): string[] {
  return ACHIEVEMENTS.filter((a) => {
    const [current, target] = a.progress(stats);
    return current >= target;
  }).map((a) => a.id);
}
